import { useEffect, useMemo, useState } from 'react'
import BaseWidgetProvider from './base/BaseWidgetProvider'
import { apiGet } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import {
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  Divider,
  Alert,
  useTheme,
  alpha
} from '@mui/material'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import useMosInfo from './common/provider/useMosInfo'
import { filterVisibleDevices, normalizeHiddenDeviceIds } from './common/deviceVisibility/hiddenDeviceIds'
import DeviceVisibilityConfiguration from './common/deviceVisibility/DeviceVisibilityConfiguration'

/**
 * The list shows its search field only above this many devices, or once a term has been typed.
 * Single constant on purpose: the rule is applied in both layouts and a duplicated literal is how
 * one of them ends up counting something else (DP1 counts it over the VISIBLE list).
 */
const SEARCH_FIELD_MIN_DEVICES = 5

const WidgetDeviceSelect = ({ w, h, data }) => {
  const theme = useTheme()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const { t_ } = useSmartTranslation()

  const { navigateToDeviceId, device: currentDevice } = useMosInfo()

  // Story 157 — hiding is a subtraction over what the server returns (RN10): the request never
  // carries the hidden list, the response arrives whole and the hidden devices are removed before
  // painting. Applied here, at the single point both layouts render from (P5, RN9).
  const hiddenDeviceIds = useMemo(() => normalizeHiddenDeviceIds(data?.hiddenDeviceIds), [data?.hiddenDeviceIds])
  const visibleDevices = useMemo(() => filterVisibleDevices(devices, hiddenDeviceIds), [devices, hiddenDeviceIds])
  const showSearchField = visibleDevices.length > SEARCH_FIELD_MIN_DEVICES || Boolean(searchTerm)

  useEffect(() => {
    let mounted = true
    const delayDebounceFn = setTimeout(() => {
      const loadDevices = async () => {
        try {
          setLoading(true)
          const term = searchTerm.trim()
          const params = term ? { search: term } : ''
          const result = await apiGet('deviceIdCodeNameForUser', params)
          if (mounted) {
            setDevices(result || [])
            setError(null)
          }
        } catch (err) {
          if (mounted) {
            console.error('Error loading devices:', err)
            setError(t_('Error loading devices'))
          }
        } finally {
          if (mounted) {
            setLoading(false)
          }
        }
      }
      loadDevices()
    }, 500)

    return () => {
      mounted = false
      clearTimeout(delayDebounceFn)
    }
  }, [searchTerm, t_])

  const handleDeviceClick = (deviceId) => {
    navigateToDeviceId(deviceId)
    setAnchorEl(null)
    setSearchTerm('')
  }

  const renderDeviceList = (isMenu = false) => {
    if (loading) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' p={3} minHeight={100}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    if (error) {
      return (
        <Box p={2}>
          <Alert severity='error' variant='outlined' sx={{ alignItems: 'center' }}>
            {error}
          </Alert>
        </Box>
      )
    }

    if (visibleDevices.length === 0) {
      return (
        <Box p={3} textAlign='center'>
          <Typography variant='body2' color='text.secondary'>
            {searchTerm ? t_('No devices found') : t_('No devices available')}
          </Typography>
        </Box>
      )
    }

    const listContent = visibleDevices.map((device) => {
      const isSelected = currentDevice?.id === device.id
      const ItemComponent = isMenu ? MenuItem : ListItemButton

      return (
        <ItemComponent
          key={device.id}
          selected={isSelected}
          onClick={() => handleDeviceClick(device.id)}
          sx={{
            borderRadius: isMenu ? 0 : 1,
            mb: isMenu ? 0 : 0.5,
            minHeight: 60,
            py: 1.8,
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.25)
              }
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.primary, 0.05)
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: isSelected ? 'primary.main' : 'text.secondary' }}>
            {isSelected ? <CheckCircleIcon fontSize='small' /> : <PrecisionManufacturingIcon fontSize='small' />}
          </ListItemIcon>
          <ListItemText
            primary={device.name}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'text.primary',
              noWrap: true
            }}
            secondary={device.code}
            secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
          />
        </ItemComponent>
      )
    })

    return (
      <List sx={{ p: isMenu ? 0 : 1, width: '100%' }}>
        {listContent}
      </List>
    )
  }

  // Compact Mode (Widget Width <= 1)
  if (w <= 1) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'transparent',
          borderRadius: 1
        }}
      >
        <Tooltip title={t_('Select Device')}>
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            color={currentDevice ? 'primary' : 'default'}
            size={h > 1 ? 'large' : 'medium'}
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 1,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
            }}
          >
            <PrecisionManufacturingIcon fontSize='inherit' />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              maxHeight: 300,
              width: 250,
              mt: 1
            }
          }}
        >
          <Box px={2} py={1} position='sticky' top={0} bgcolor='background.paper' zIndex={1}>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              {t_('Devices')}
            </Typography>
            {showSearchField && (
              <TextField
                size='small'
                placeholder={t_('Search devices...')}
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' color='action' />
                    </InputAdornment>
                  )
                }}
              />
            )}
          </Box>
          <Divider />
          {renderDeviceList(true)}
        </Menu>
      </Box>
    )
  }

  // Expanded Mode (Widget Width > 1)
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'transparent'
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >

        {showSearchField && (
          <TextField
            size='small'
            placeholder={t_('Search devices...')}
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' color='action' />
                </InputAdornment>
              ),
              sx: {
                fontSize: '0.875rem',
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                '&:hover': {
                  bgcolor: alpha(theme.palette.background.paper, 0.8)
                }
              }
            }}
          />
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {renderDeviceList(false)}
      </Box>

      {currentDevice && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            <strong>{currentDevice.name}</strong>
          </Typography>
        </Box>
      )}
    </Box>
  )
}

class WidgetDeviceSelectProvider extends BaseWidgetProvider {
  getCode = () => 'WidgetDeviceSelectProvider'

  getName = () => 'Device selector'

  getIcon = () => PrecisionManufacturingIcon

  createWidget = (props) => <WidgetDeviceSelect {...props} />

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight), availableWithoutDevice: true }
  }

  /**
   * The hidden list travels wherever the widget configuration travels (RN29), so the default this
   * provider declares is what gets written when a template is created "without copying the
   * settings", when an invalid configuration is reset and when an unreadable one is degraded
   * (RN36). An empty list, never a null nor an object without the key.
   */
  getDefaultConfiguration = () => ({ hiddenDeviceIds: [] })

  /**
   * This widget had no settings panel at all: its gear button is new (DP9). The frame only paints
   * it when the provider offers a panel and the screen is editable, so the existing MOS edit
   * permission keeps governing it — no new authorization (RN31).
   */
  createConfigurationWidget = (data, setData, t_) => (
    <DeviceVisibilityConfiguration
      data={data}
      setData={setData}
      t_={t_}
      loadOfferableDevices={() => apiGet('deviceIdCodeNameForUser')}
      emptyOfferableMessage='No devices available'
    />
  )
}

export { WidgetDeviceSelect }
export default WidgetDeviceSelectProvider
